import React from "react";
import { useAppDispatch, useAppSelector } from "../../../redux";
import {
  Modal,
  ModalVariants,
  Button,
  Loader,
  Avatar,
  AvatarSize,
  TextField,
  Dropdown,
  Toggle,
  Iconography,
  IconographySizes,
  IconographyThemes,
  LoaderSizes,
} from "@hid-galaxy-ui/galaxy-react";
import "./PartnerUserFormModal.scss";
import parsePhoneNumberFromString from "libphonenumber-js";
import { CountryCode } from "../../../utils/CountryCode";
import {
  getUserFormDropdownMenuProps,
  USER_FORM_MODAL_BODY_CLASS,
} from "../../utils/userFormModalDropdown";
import {
  addUserSagaAction,
  updateUserSagaAction,
  getUserDetailSagaAction,
  getPartnerRolesSagaAction,
  UserListRefreshTarget,
} from "../../../redux/saga/user/saga-actions";
import { userSliceAction } from "../../../redux/slices/user";
import { getPartnerDropdownSagaAction } from "../../../redux/saga/partner-details/saga-actions";
import { PartnerRoleType, UserListType } from "../../../redux/slices/user/Types";
import { usePartnerTabPermission } from "../../../contexts/PartnerPermissionContext";
import { useSessionCanEditModule } from "../../../hooks/useSessionPermissions";
import { PARTNER_MANAGEMENT_PERMISSION_KEYS as PM_KEYS } from "../../../utils/permissions/partnerManagementPermissionKeys";

const CountryCodeOptions = CountryCode.map((country) => {
  return {
    label: country.name,
    value: country.dial_code,
  };
});

const CountryOptions = CountryCode.map((country) => {
  return { label: country.name, value: country.name };
});

/** Legacy insert API expects role slug e.g. Partner_Admin; UI still uses API display name. */
const formatPartnerRoleForInsertApi = (roleName: string): string =>
  roleName.trim().replace(/\s+/g, "_");

const getPartnerRoleIcon = (roleName: string): string => {
  const normalized = (roleName ?? "").trim().toLowerCase();
  if (normalized.includes("admin")) return "userLock";
  if (normalized.includes("developer")) return "rectangleCode";
  if (normalized.includes("manager")) return "userGroup";
  if (normalized.includes("consultant")) return "userCheck";
  return "user";
};

type PartnerDropdownOption = { label: string; value: number };

const normalizePartnerId = (value: unknown): number | null => {
  if (value === undefined || value === null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const PartnerUserFormModal: React.FC<{
  user?: UserListType[0];
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  mode: "Add" | "Update";
  allowPartnerSelect?: boolean;
  listRefreshTarget?: UserListRefreshTarget;
}> = ({
  user,
  setModalOpen,
  mode,
  allowPartnerSelect = false,
  listRefreshTarget,
}) => {
  const [userDetails, setUserDetails] = React.useState({
    firstName: "",
    lastName: "",
    email: "",
    dialCode: "",
    phoneNumber: "",
    role: "",
    roleId: null as number | null,
    address: "",
    city: "",
    state: "",
    country: "",
    zip: "",
    partnerId: null as number | null,
  });
  const [view, setView] = React.useState<"detail" | "role" | "status">(
    "detail"
  );
  const [formSubmitted, setFormSubmitted] = React.useState(false);
  const [emailStatus, setEmailStatus] = React.useState<
    { status: "success" | "error" } | {}
  >({});
  const [phoneStatus, setPhoneStatus] = React.useState<
    { status: "success" | "error" } | {}
  >({});
  const [errorMsg, setErrorMsg] = React.useState({
    emailMsg: "",
    phoneMsg: "",
  });
  const [selected, setSelected] = React.useState(null);
  const [selectedCountry, setSelectedCountry] = React.useState(null);
  const [selectedPartner, setSelectedPartner] =
    React.useState<PartnerDropdownOption | null>(null);
  const userInitializedRef = React.useRef<number | null>(null);
  const appliedUserDetailKeyRef = React.useRef<string | null>(null);
  const { partnerDetails, partnerDropdownOptions, status: partnerStatus } =
    useAppSelector((state) => state.partner_details);
  const {
    userDetail: initialUserDetail,
    partnerRoles,
    status,
  } = useAppSelector((state) => state.user);
  const { readOnly: tabReadOnly } = usePartnerTabPermission();
  const canEditUsers = useSessionCanEditModule(PM_KEYS.MANAGE_PARTNER_USER);
  const CreateOrEditUserPermission = canEditUsers && !tabReadOnly;
  const dispatch = useAppDispatch();
  const dropdownMenuProps = React.useMemo(() => getUserFormDropdownMenuProps(), []);
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const digitRegex = /^\d+$/;
  const headerText =
    view === "status"
      ? ""
      : view === "detail"
      ? "User Details"
      : "Assign Role and Permissions";

  const applyRoleSelection = React.useCallback(
    (roleId: number | null, roleName = "") => {
      setUserDetails((prev) => ({
        ...prev,
        roleId,
        role: roleName,
      }));
    },
    []
  );

  const resolveRoleFromPartnerRoles = React.useCallback(
    (roleId?: number | null, roleName?: string) => {
      if (roleId != null) {
        const byId = partnerRoles.find((role) => role.ID === roleId);
        if (byId) return { roleId: byId.ID, roleName: byId.name };
      }
      if (roleName) {
        const byName = partnerRoles.find((role) => role.name === roleName);
        if (byName) return { roleId: byName.ID, roleName: byName.name };
      }
      return { roleId: roleId ?? null, roleName: roleName ?? "" };
    },
    [partnerRoles]
  );

  React.useEffect(() => {
    dispatch(userSliceAction.resetUserFormStatus());
    return () => {
      dispatch(userSliceAction.resetUserFormStatus());
    };
  }, [dispatch]);

  React.useEffect(() => {
    document.body.classList.add(USER_FORM_MODAL_BODY_CLASS);
    return () => {
      document.body.classList.remove(USER_FORM_MODAL_BODY_CLASS);
    };
  }, []);

  const resolvePartnerOption = React.useCallback(
    (partnerId: number | null) => {
      if (partnerId == null) return null;
      return (
        partnerDropdownOptions.find((option) => option.value === partnerId) ??
        null
      );
    },
    [partnerDropdownOptions]
  );

  const syncPartnerSelection = React.useCallback(
    (partnerId: number | null) => {
      if (partnerId == null) {
        setSelectedPartner(null);
        setUserDetails((prev) =>
          prev.partnerId === null ? prev : { ...prev, partnerId: null }
        );
        return;
      }

      const partnerOption = resolvePartnerOption(partnerId);
      if (partnerOption) {
        setSelectedPartner(partnerOption);
      }
      setUserDetails((prev) =>
        prev.partnerId === partnerId ? prev : { ...prev, partnerId }
      );
    },
    [resolvePartnerOption]
  );

  const resetModalState = React.useCallback(() => {
    userInitializedRef.current = null;
    appliedUserDetailKeyRef.current = null;
    setSelectedPartner(null);
    setSelected(null);
    setSelectedCountry(null);
    dispatch(userSliceAction.resetUserDetail());
  }, [dispatch]);

  React.useEffect(() => {
    dispatch(getPartnerRolesSagaAction());
    if (allowPartnerSelect) {
      dispatch(getPartnerDropdownSagaAction());
    }
  }, [dispatch, allowPartnerSelect]);

  React.useEffect(() => {
    if (mode !== "Update" || !user?.ID) return;

    resetModalState();
    dispatch(getUserDetailSagaAction(user.ID));
  }, [mode, user?.ID, dispatch, resetModalState]);

  React.useEffect(() => {
    if (!user || formSubmitted || view === "status") return;
    if (userInitializedRef.current === user.ID) return;

    userInitializedRef.current = user.ID;

    const parsedPhoneNumber = parsePhoneNumberFromString(
      user.phone_number ?? ""
    );
    let dialCode = "";
    let nationalNo = "";
    if (parsedPhoneNumber) {
      dialCode = "+" + parsedPhoneNumber.countryCallingCode;
      nationalNo = parsedPhoneNumber.nationalNumber;
      const country = CountryCode.find(
        (country) => country.code === parsedPhoneNumber.country
      );
      if (country)
        setSelected({
          label: country.name,
          value: country.dial_code,
        });
    }
    const { roleId, roleName } = resolveRoleFromPartnerRoles(
      user.role_id,
      user.role
    );
    setUserDetails((prev) => {
      return {
        ...prev,
        firstName: user.first_name,
        lastName: user.last_name,
        email: user.email,
        dialCode: dialCode,
        phoneNumber: nationalNo,
        role: roleName,
        roleId,
      };
    });

    if (allowPartnerSelect) {
      syncPartnerSelection(normalizePartnerId(user.metadata?.partner));
    }
  }, [
    user,
    allowPartnerSelect,
    resolveRoleFromPartnerRoles,
    syncPartnerSelection,
    formSubmitted,
    view,
  ]);

  React.useEffect(() => {
    if (
      mode !== "Update" ||
      !initialUserDetail?.ID ||
      initialUserDetail.ID !== user?.ID ||
      formSubmitted ||
      view === "status"
    ) {
      return;
    }

    const partnerId = normalizePartnerId(initialUserDetail.metadata?.partner);
    const detailKey = `${initialUserDetail.ID}:${partnerId ?? "none"}:${initialUserDetail.updated_at ?? ""}`;
    if (appliedUserDetailKeyRef.current === detailKey) return;

    appliedUserDetailKeyRef.current = detailKey;

    const country = CountryOptions.find(
      (country) => country.value === initialUserDetail.country
    );
    if (country) setSelectedCountry(country);

    if (allowPartnerSelect) {
      syncPartnerSelection(partnerId);
    }

    const { roleId, roleName } = resolveRoleFromPartnerRoles(
      initialUserDetail.role_id,
      initialUserDetail.role
    );
    applyRoleSelection(roleId, roleName);
    setUserDetails((prev) => {
      return {
        ...prev,
        country: initialUserDetail.country,
        state: initialUserDetail.province,
        city: initialUserDetail.city,
        zip: initialUserDetail.zip,
        address: initialUserDetail.address1,
        partnerId: allowPartnerSelect ? partnerId : prev.partnerId,
      };
    });
  }, [
    initialUserDetail,
    mode,
    user?.ID,
    allowPartnerSelect,
    resolveRoleFromPartnerRoles,
    applyRoleSelection,
    syncPartnerSelection,
    formSubmitted,
    view,
  ]);

  React.useEffect(() => {
    if (emailRegex.test(userDetails.email)) {
      setEmailStatus({});
      setErrorMsg({
        ...errorMsg,
        emailMsg: "",
      });
    }
  }, [userDetails.email]);

  React.useEffect(() => {
    if (userDetails.phoneNumber.length === 0) {
      setPhoneStatus({});
      setErrorMsg({ ...errorMsg, phoneMsg: "" });
    } else {
      const parsedPhoneNumber = parsePhoneNumberFromString(
        userDetails.dialCode + userDetails.phoneNumber
      );
      if (
        parsedPhoneNumber &&
        parsedPhoneNumber.isValid() &&
        digitRegex.test(userDetails.phoneNumber)
      ) {
        setPhoneStatus({});
        setErrorMsg({ ...errorMsg, phoneMsg: "" });
      }
    }
  }, [userDetails.phoneNumber, userDetails.dialCode]);

  const handleChange = (event: React.FormEvent<HTMLInputElement>): void => {
    setUserDetails({
      ...userDetails,
      [event.currentTarget.name]: event.currentTarget.value,
    });
  };

  const validateEmail = () => {
    if (!emailRegex.test(userDetails.email)) {
      setEmailStatus({ status: "error" });
      setErrorMsg({
        ...errorMsg,
        emailMsg: "Enter valid email-id",
      });
    }
  };

  const validatePhone = () => {
    const parsedPhoneNumber = parsePhoneNumberFromString(
      userDetails.dialCode + userDetails.phoneNumber
    );
    if (userDetails.phoneNumber.length > 0) {
      if (
        !parsedPhoneNumber ||
        !parsedPhoneNumber.isValid() ||
        !digitRegex.test(userDetails.phoneNumber)
      ) {
        setPhoneStatus({ status: "error" });
        setErrorMsg({ ...errorMsg, phoneMsg: "Enter a valid phone number" });
      }
    }
  };

  const onCountryCodeSelect = (data: { label: string; value: string }) => {
    let value = data ? data.value : "";
    setSelected(data);
    setUserDetails({
      ...userDetails,
      dialCode: value,
    });
    if (data) {
      const parsedPhoneNumber = parsePhoneNumberFromString(
        data.value + userDetails.phoneNumber
      );
      if (userDetails.phoneNumber.length > 0) {
        if (!parsedPhoneNumber || !parsedPhoneNumber.isValid()) {
          setPhoneStatus({ status: "error" });
          setErrorMsg({ ...errorMsg, phoneMsg: "Enter a valid phone number" });
        }
      }
    }
  };

  const onCountrySelect = (data: { label: string; value: string }) => {
    let value = data ? data.value : "";
    setSelectedCountry(data);
    setUserDetails({
      ...userDetails,
      country: value,
    });
  };

  const onPartnerSelect = (data: PartnerDropdownOption | null) => {
    if (mode === "Update") return;
    if (!data) {
      setSelectedPartner(null);
      setUserDetails({ ...userDetails, partnerId: null });
      return;
    }
    setSelectedPartner(data);
    setUserDetails({ ...userDetails, partnerId: data.value });
  };

  const resolvePartnerId = () => {
    if (allowPartnerSelect) return userDetails.partnerId;
    return partnerDetails?.ID;
  };

  const addOrUpdateUser = () => {
    const partnerId = resolvePartnerId();
    if (!partnerId) return;

    const sagaOptions = listRefreshTarget ? { listRefreshTarget } : undefined;

    if (mode === "Add") {
      const submitData = {
        first_name: userDetails.firstName,
        last_name: userDetails.lastName,
        email: userDetails.email,
        phone_number: userDetails.dialCode + userDetails.phoneNumber,
        status: "Invited",
        role: formatPartnerRoleForInsertApi(userDetails.role),
        role_id: userDetails.roleId ?? undefined,
        address1: userDetails.address,
        city: userDetails.city,
        province: userDetails.state,
        country: userDetails.country,
        zip: userDetails.zip,
        partner: partnerId,
      };
      dispatch(addUserSagaAction(submitData, sagaOptions));
    } else {
      const submitData = {
        ID: initialUserDetail.ID,
        first_name: userDetails.firstName,
        last_name: userDetails.lastName,
        email: initialUserDetail.email,
        phone: userDetails.dialCode + userDetails.phoneNumber,
        status: initialUserDetail.status,
        role_id: userDetails.roleId ?? undefined,
        address1: userDetails.address,
        city: userDetails.city,
        province: userDetails.state,
        country: userDetails.country,
        zip: userDetails.zip,
        partner: partnerId,
      };
      dispatch(updateUserSagaAction(submitData, sagaOptions));
    }
  };

  const RoleCard: React.FC<{ role: PartnerRoleType }> = ({ role }) => {
    const toggled = userDetails.roleId === role.ID;
    const handleToggle = (e: any) => {
      if (e.target.checked) {
        applyRoleSelection(role.ID, role.name);
      } else {
        applyRoleSelection(null, "");
      }
    };

    return (
      <div className="role-card">
        <div className="header">
          <div className="name">
            <Iconography
              icon={getPartnerRoleIcon(role.name)}
              size={IconographySizes.Medium}
            />
            <p>{role.name}</p>
          </div>
          <Toggle
            toggled={toggled}
            labelOFF="Disabled"
            labelON="Enabled"
            disabled={!CreateOrEditUserPermission}
            onClick={(e) => handleToggle(e)}
          />
        </div>
        {role.description ? (
          <div className="body">
            <p>{role.description}</p>
          </div>
        ) : null}
      </div>
    );
  };

  const isNextButtonEnabled =
    userDetails.firstName &&
    userDetails.lastName &&
    emailRegex.test(userDetails.email) &&
    (!allowPartnerSelect || userDetails.partnerId != null);

  const isAddOrUpdateButtonEnabled =
    userDetails.roleId != null && CreateOrEditUserPermission;

  const handleClose = () => {
    setView("detail");
    setFormSubmitted(false);
    dispatch(userSliceAction.resetUserFormStatus());
    resetModalState();
    setModalOpen(false);
  };

  const handleSubmit = () => {
    setFormSubmitted(true);
    setView("status");
    addOrUpdateUser();
  };

  const isSubmitting = status.addUserLoading || status.updateUserLoading;

  const renderView = (view: "detail" | "role" | "status") => {
    const statusBoolean =
      mode === "Add" ? status.addUserStatus : status.updateUserStatus;
    const statusMessage =
      mode === "Add" ? status.addUserMessage : status.updateUserMessage;
    switch (view) {
      case "detail":
        return (
          <>
            <form
              className="details-form"
              autoComplete="off"
              onSubmit={(event) => event.preventDefault()}
            >
              <Avatar size={AvatarSize.XLarge} isEditable={false} />
              <div className="input-row-wrapper">
                <div className="input-col-wrapper">
                  <div className="label-field-container">
                    <label className="field-label">
                      First Name<span>*</span>
                    </label>
                    <TextField
                      id="partner-user-first-name"
                      name="firstName"
                      autoComplete="given-name"
                      placeholder=""
                      value={userDetails.firstName}
                      theme="dark"
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="input-col-wrapper">
                  <div className="label-field-container">
                    <label className="field-label">
                      Last Name<span>*</span>
                    </label>
                    <TextField
                      id="partner-user-last-name"
                      name="lastName"
                      autoComplete="family-name"
                      placeholder=""
                      value={userDetails.lastName}
                      theme="dark"
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
              <div className="input-row-wrapper">
                <div className="label-field-container">
                  <label className="field-label">
                    E-Mail ID<span>*</span>
                  </label>
                  <TextField
                    {...emailStatus}
                    helperText={errorMsg.emailMsg}
                    id="partner-user-email"
                    name="email"
                    autoComplete="email"
                    placeholder=""
                    value={userDetails.email}
                    theme="dark"
                    onChange={handleChange}
                    onBlur={validateEmail}
                    disabled={mode === "Update"}
                  />
                </div>
              </div>
              {allowPartnerSelect ? (
                <div className="input-row-wrapper">
                  <div className="label-field-container">
                    <label className="field-label">
                      Partner Name<span>*</span>
                    </label>
                    {mode === "Update" ? (
                      <TextField
                        id="partner-user-partner-readonly"
                        name="partner-user-partner-readonly"
                        value={selectedPartner?.label ?? user?.partner_name ?? "—"}
                        theme="dark"
                        disabled
                      />
                    ) : partnerStatus.partnerDropdownLoading &&
                      partnerDropdownOptions.length === 0 ? (
                      <Loader variant={LoaderSizes.small} />
                    ) : (
                      <Dropdown
                        id="partner-user-partner"
                        name="partner-user-partner"
                        placeholder="Select Partner"
                        options={partnerDropdownOptions}
                        theme="dark"
                        selected={selectedPartner}
                        onSelect={onPartnerSelect}
                        {...dropdownMenuProps}
                      />
                    )}
                  </div>
                </div>
              ) : null}
              <div className="input-row-wrapper">
                <div className="label-field-container">
                  <label className="field-label">Phone Number</label>
                  <div style={{ display: "flex", gap: "16px" }}>
                    <div className="dropdown-wrapper">
                      <Dropdown
                        id="partner-user-country-code"
                        name="partner-user-country-code"
                        placeholder="Country Code"
                        options={CountryCodeOptions}
                        selected={selected}
                        onSelect={onCountryCodeSelect}
                        isClearable={true}
                        {...dropdownMenuProps}
                      />
                    </div>
                    <div className="phone-wrapper">
                      <TextField
                        {...phoneStatus}
                        id="partner-user-phone"
                        name="phoneNumber"
                        autoComplete="off"
                        placeholder="Phone number"
                        value={userDetails.phoneNumber}
                        theme="dark"
                        helperText={errorMsg.phoneMsg}
                        onChange={handleChange}
                        onBlur={validatePhone}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="input-row-wrapper">
                <div className="input-col-wrapper">
                  <div className="label-field-container">
                    <label className="field-label">Country</label>
                    <Dropdown
                      id="partner-user-country"
                      name="partner-user-country"
                      placeholder="Country"
                      options={CountryOptions}
                      selected={selectedCountry}
                      onSelect={onCountrySelect}
                      isClearable={true}
                      {...dropdownMenuProps}
                    />
                  </div>
                </div>
                <div className="input-col-wrapper">
                  <div className="label-field-container">
                    <label className="field-label">State</label>
                    <TextField
                      id="partner-user-state"
                      name="state"
                      autoComplete="off"
                      placeholder=""
                      value={userDetails.state}
                      theme="dark"
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
              <div className="input-row-wrapper">
                <div className="input-col-wrapper">
                  <div className="label-field-container">
                    <label className="field-label">City</label>
                    <TextField
                      id="partner-user-city"
                      name="city"
                      autoComplete="off"
                      value={userDetails.city}
                      theme="dark"
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="input-col-wrapper">
                  <div className="label-field-container">
                    <label className="field-label">Zip Code</label>
                    <TextField
                      id="partner-user-zip"
                      name="zip"
                      autoComplete="off"
                      placeholder=""
                      value={userDetails.zip}
                      theme="dark"
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
              <div className="input-row-wrapper">
                <div className="label-field-container">
                  <label className="field-label">Street Address</label>
                  <TextField
                    id="partner-user-address"
                    name="address"
                    autoComplete="off"
                    placeholder=""
                    value={userDetails.address}
                    theme="dark"
                    onChange={handleChange}
                  />
                </div>
              </div>
            </form>
            <div className="button-group">
              <Button
                variant="secondary"
                label="Cancel"
                onClick={handleClose}
              />
              <Button
                variant="primary"
                label="Next"
                disabled={!isNextButtonEnabled}
                onClick={() => {
                  setView("role");
                }}
              />
            </div>
          </>
        );
      case "role":
        return (
          <>
            <div className="user-info">
              <Avatar />
              <div className="name">{`${userDetails.firstName} ${userDetails.lastName}`}</div>
              <div className="email">{userDetails.email}</div>
            </div>
            <div className="role-list">
              {status.partnerRolesLoading ? (
                <div className="loading">
                  <Loader variant={LoaderSizes.large} />
                </div>
              ) : partnerRoles.length > 0 ? (
                partnerRoles.map((role) => (
                  <RoleCard role={role} key={role.ID} />
                ))
              ) : (
                <p>No partner roles available.</p>
              )}
            </div>
            <div className="button-group">
              <Button
                variant="secondary"
                label="Back"
                onClick={() => setView("detail")}
              />
              <Button
                variant="primary"
                label={
                  mode === "Add" ? "Add And Assign Role To User" : "Update User"
                }
                disabled={!isAddOrUpdateButtonEnabled}
                onClick={handleSubmit}
              />
            </div>
          </>
        );
      default:
        return (
          <div className="status-container">
            {isSubmitting || !formSubmitted ? (
              <div className="loading">
                <Loader variant={LoaderSizes.large} />
              </div>
            ) : statusBoolean ? (
              <>
                <Iconography
                  icon="circleCheck"
                  size={IconographySizes.XLarge}
                  theme={IconographyThemes.Success}
                  className="status-icon"
                />
                <div className="message">
                  {mode === "Add"
                    ? "User Added with Invitation Sent"
                    : "User Updated"}
                </div>
                <div className="user-info" style={{ gap: "8px" }}>
                  <div className="name">{`${userDetails.firstName} ${userDetails.lastName}`}</div>
                  <div className="email">{userDetails.email}</div>
                  <div className="role">{userDetails.role}</div>
                </div>
                <Button
                  variant="primary"
                  label="Close"
                  onClick={handleClose}
                />
              </>
            ) : (
              <>
                <Iconography
                  icon="circleXmark"
                  size={IconographySizes.XLarge}
                  theme={IconographyThemes.Error}
                  className="status-icon"
                />
                <div className="message">
                  {statusMessage || "Something went wrong!"}
                </div>
                <Button
                  variant="primary"
                  label="Close"
                  onClick={handleClose}
                />
              </>
            )}
          </div>
        );
    }
  };

  return (
    <Modal
      headerText={headerText}
      width={view === "role" ? 920 : 500}
      onClose={handleClose}
      className="partner-user-form-modal"
    >
      <div className="user-form-container">{renderView(view)}</div>
    </Modal>
  );
};

export default PartnerUserFormModal;
